import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { ExternalLink, Save, Eye, EyeOff, Lock, Link2, Check } from "lucide-react";
import iziToast from "izitoast";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  saveDistributorConfigAPI,
  getDistributorConfigAPI,
} from "actions/bomItemActions";
import { BASE_URL } from "constants/apiConstant";

// Constants from distributors.form.js
const STATUS = [
  { name: "Enabled", value: 1, color: "bg-success" },
  { name: "Disabled", value: 2, color: "bg-danger" },
];

const DISTRIBUTOR_STATUS = [
  { name: "Mouser", value: "Mouser" },
  { name: "Digikey", value: "Digikey" },
  { name: "TTI", value: "TTI" },
  { name: "Arrow", value: "Arrow" },
  { name: "TI", value: "TI" },
  { name: "Element14", value: "Element14" },
  { name: "TME", value: "TME" },
  { name: "Rutronik", value: "Rutronik" },
  { name: "Avnet", value: "Avnet" },
];

const MOUSER_ORDER_ENVIRONMENT = [
  { name: "Production", value: 1 },
  { name: "Sandbox", value: 2 },
];

const DIGIKEY_ENVIRONMENT = [
  { name: "Production", value: 1 },
  { name: "Sandbox", value: 2 },
];

// Build the OAuth redirect_uri from the app's shared BASE_URL (constants/
// apiConstant) so it always follows the configured environment. The digikey /
// element14 callbacks live at the server root, so use BASE_URL with the
// trailing slash stripped.
// An OAuth redirect_uri must be ABSOLUTE: the provider, not the browser,
// resolves it. BASE_URL is "/" on the deployments where the API is same-origin
// (REACT_APP_API_BASE_URL=/), which stripped to "" and produced the bare path
// "/digikey/callback". Appended to DigiKey's authorize URL that read as
// api.digikey.com/digikey/callback, so DigiKey sent the code to ITSELF and
// answered 404. Anchor a relative BASE_URL to this page's origin.
// …and it must point at the route that EXISTS. Laravel registers routes/api.php
// under the "api" prefix, so the callback is /api/digikey/callback — the bare
// /digikey/callback this used to build is a 404 on our own host too. The
// backend already rebuilds it with APP_URL + "api/…" for the token exchange,
// and the two must agree or DigiKey rejects the exchange.
const API_URL = (() => {
  const base = (BASE_URL || "").replace(/\/+$/, "");
  const abs = /^https?:\/\//i.test(base)
    ? base
    : `${typeof window !== "undefined" ? window.location.origin : ""}${base}`;
  return /\/api$/.test(abs) ? abs : `${abs}/api`;
})();

// Resolve the environment option list for a given form key.
// TI's order API has a dedicated /test path that validates and prices an order
// without placing it, which is what "Test" selects here.
const TI_ORDER_ENVIRONMENT = [
  { name: "Production", value: 1 },
  { name: "Test", value: 2 },
];

const ENV_BY_KEY = {
  mouser_order_environment: MOUSER_ORDER_ENVIRONMENT,
  digikey_environment: DIGIKEY_ENVIRONMENT,
  ti_order_environment: TI_ORDER_ENVIRONMENT,
};

// Per-distributor credential layout for the modal (mirrors the prototype).
// `key` maps to a field on distributorForm so the save payload is unchanged.
const FIELD_CONFIG = {
  Mouser: {
    envKey: "mouser_order_environment",
    envLabel: "Order environment",
    fields: [
      { key: "mouser_api_key", label: "Search API key", secret: true, ph: "Enter the Mouser search API key" },
      { key: "mouser_order_api_key", label: "Order API key", secret: true, ph: "Enter the Mouser order API key" },
    ],
  },
  Digikey: {
    envKey: "digikey_environment",
    envLabel: "Environment",
    oauthLabel: "OAuth 2.0",
    fields: [
      { key: "digikey_client_id", label: "Client ID", ph: "Enter the Digi-Key client ID" },
      { key: "digikey_secret", label: "Client secret", secret: true, ph: "Enter the Digi-Key client secret" },
      { key: "customer_id", label: "Customer ID", ph: "Enter the Digi-Key customer ID" },
      { key: "billing_account", label: "Billing account", ph: "Enter the billing account number" },
    ],
  },
  // Element14 is production-only (see getElement14Url in the API), so it has
  // no environment selector.
  Element14: {
    oauthLabel: "Auth token",
    fields: [
      { key: "element14_api_key", label: "Search product API key", secret: true, ph: "Enter the search product API key" },
      { key: "element14_generate_token_api_key", label: "Generate token API key", secret: true, ph: "Enter the token API key" },
      { key: "element14_trade_account", label: "Trade account", ph: "Enter the Element 14 trade account" },
    ],
  },
  // Arrow has no order-placement API; orders go out by email, like TME/Rutronik.
  Arrow: {
    fields: [
      { key: "arrow_api_key", label: "API key", secret: true, ph: "Enter the Arrow API key" },
      { key: "arrow_api_username", label: "API username", ph: "Enter the Arrow API username" },
      { key: "arrow_order_email", label: "Order Email", secret: false, ph: "Enter the Arrow order email" },
    ],
  },
  // TTI has no order-placement API; orders go out by email, like TME/Rutronik.
  TTI: {
    fields: [
      { key: "tti_api_key", label: "API key", secret: true, ph: "Enter the TTI API key" },
      { key: "tti_customer_account_number", label: "Customer account number", ph: "Enter the TTI customer account number" },
      { key: "tti_order_email", label: "Order Email", secret: false, ph: "Enter the TTI order email" },
    ],
  },
  TME: {
    fields: [
      { key: "tme_token", label: "Token", secret: true, ph: "Enter the TME token" },
      { key: "tme_secret", label: "Secret", secret: true, ph: "Enter the TME secret" },
      { key: "tme_order_email", label: "Order Email", secret: false, ph: "Enter the TME order email" },
    ],
  },
  // TI search reads the public ti.com endpoints and needs no credentials. These
  // are only for the transact.ti.com store order API, so all of them are
  // optional unless you intend to place orders through TI.
  TI: {
    envKey: "ti_order_environment",
    envLabel: "Order environment",
    fields: [
      { key: "ti_client_id", label: "Order API client id", ph: "Enter the TI order API client id" },
      { key: "ti_secret", label: "Order API secret", secret: true, ph: "Enter the TI order API secret" },
      { key: "ti_checkout_profile_id", label: "Checkout profile id", ph: "Enter the TI checkout profile id" },
    ],
  },
  Rutronik: {
    fields: [
      { key: "rutronik_api_key", label: "API key", secret: true, ph: "Enter the Rutronik API key" },
      { key: "rutronik_order_email", label: "Order Email", secret: false, ph: "Enter the Rutronik order email" },
    ],
  },
  // Avnet has no order-placement API; orders go out by email, like TME/Rutronik.
  Avnet: {
    fields: [
      { key: "avnet_api_key", label: "API key", secret: true, ph: "Enter the Avnet API key" },
      { key: "avnet_wc_token", label: "WC token (optional)", secret: true, ph: "Enter the Avnet WCToken, if issued" },
      { key: "avnet_wc_trusted_token", label: "WC trusted token (optional)", secret: true, ph: "Enter the Avnet WCTrustedToken, if issued" },
      { key: "avnet_order_email", label: "Order Email", secret: false, ph: "Enter the Avnet order email" },
    ],
  },
};

// Credential input with optional show/hide for secret values.
const SecretInput = ({ value, onChange, placeholder, secret }) => {
  const [shown, setShown] = useState(false);
  if (!secret) {
    return (
      <Input
        type="text"
        className="h-10 border-s360-stroke1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    );
  }
  return (
    <div className="relative">
      <Input
        type={shown ? "text" : "password"}
        className="h-10 pr-10 border-s360-stroke1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? "Hide" : "Show"}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-s360-fg3 hover:text-s360-fg1"
      >
        {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const DistributorConfiguration = ({
  selectedDistributor = "",
  isModal = false,
  onSaveSuccess = null,
  initialValues = null,
}) => {
  const user = useSelector((state) => state?.user?.user);
  // Auth token used to build the Digi-Key / Element 14 OAuth redirect, mirroring
  // admin-ui's distributors.form.js which reads it from the store.
  const reduxToken = useSelector((state) => state?.user?.token);
  const authToken = reduxToken || localStorage.getItem("token") || "";
  // Real distributor record id (present when editing an existing distributor) —
  // used in the OAuth redirect config instead of a hardcoded "1". Falls back to
  // "1" only for a brand-new, unsaved distributor.
  const distributorRecordId = initialValues?.id || "1";
  const [isLoading, setIsLoading] = useState(false);
  const [showOauthUrl, setShowOauthUrl] = useState(false);

  // Distributor form state
  const [distributorForm, setDistributorForm] = useState({
    status: 1,
    type: selectedDistributor || "",
    mouser_order_environment: 1,
    element14_environment: 1,
    digikey_environment: 1,
    mouser_api_key: "",
    mouser_order_api_key: "",
    tti_api_key: "",
    arrow_api_key: "",
    element14_api_key: "",
    element14_generate_token_api_key: "",
    element14_trade_account: "",
    element14_auth_token: "",
    element14_token_date_time: "",
    digikey_client_id: "",
    digikey_secret: "",
    digikey_access_token: "",
    digikey_token_date_time: "",
    digikey_token_expiry: "",
    digikey_refresh_token: "",
    digikey_refresh_token_expiry: "",
    digikey_refresh_token_date_time: "",
    tti_customer_account_number: "",
    tme_token: "",
    tme_secret: "",
    tme_order_email: "",
    rutronik_api_key: "",
    rutronik_order_email: "",
    ti_client_id: "",
    ti_secret: "",
    ti_checkout_profile_id: "",
    ti_order_environment: 1,
    arrow_api_username: "",
    arrow_order_email: "",
    tti_order_email: "",
    avnet_api_key: "",
    avnet_wc_token: "",
    avnet_wc_trusted_token: "",
    avnet_order_email: "",
    name: "",
    telephone: "",
    customer_id: "",
    billing_account: "",
    shipping_method: "",
    buyer_company: "",
    buyer_first_name: "",
    buyer_last_name: "",
    buyer_email: "",
    buyer_address_line_one: "",
    buyer_address_line_two: "",
    buyer_city: "",
    buyer_province: "",
    buyer_postal_code: "",
    buyer_country: "",
    shipping_company: "",
    shipping_first_name: "",
    shipping_last_name: "",
    shipping_email: "",
    shipping_address_line_one: "",
    shipping_address_line_two: "",
    shipping_city: "",
    shipping_province: "",
    shipping_postal_code: "",
    shipping_country: "",
    country_code: "",
    consignee_name: "",
    primary_application: "",
    secondary_application: "",
    additional_info: "",
  });

  // Field visibility state
  const [fieldVisibility, setFieldVisibility] = useState(() => {
    // Initialize field visibility based on selected distributor
    const initialVisibility = {
      mouserFields: false,
      digikeyFields: false,
      ttiFields: false,
      arrowFields: false,
      tiFields: false,
      element14Fields: false,
      tmeFields: false,
      rutronikFields: false,
    };

    // Set the selected distributor's fields to true
    switch (selectedDistributor) {
      case "Mouser":
        initialVisibility.mouserFields = true;
        break;
      case "Digikey":
        initialVisibility.digikeyFields = true;
        break;
      case "TTI":
        initialVisibility.ttiFields = true;
        break;
      case "Arrow":
        initialVisibility.arrowFields = true;
        break;
      case "TI":
        initialVisibility.tiFields = true;
        break;
      case "Element14":
        initialVisibility.element14Fields = true;
        break;
      case "TME":
        initialVisibility.tmeFields = true;
        break;
      case "Rutronik":
        initialVisibility.rutronikFields = true;
        break;
      default:
        // Keep all fields false if no distributor selected
        break;
    }

    return initialVisibility;
  });

  // Form trigger state for save functionality
  const [formTriggers, setFormTriggers] = useState({
    submitTrigger: 0,
    validationTrigger: 0,
  });

  // Helper function to replace placeholders
  const replacePlaceholders = (template, replacements) => {
    let result = template;
    Object.keys(replacements).forEach((key) => {
      result = result.replace(new RegExp(`{{${key}}}`, "g"), replacements[key]);
    });
    return result;
  };

  // Handle distributor type change
  const handleDistributorTypeChange = (value) => {
    setDistributorForm((prev) => ({ ...prev, type: value }));

    let mouserFields = false;
    let digikeyFields = false;
    let ttiFields = false;
    let arrowFields = false;
    let tiFields = false;
    let element14Fields = false;
    let tmeFields = false;
    let rutronikFields = false;

    switch (value) {
      case "Mouser":
        mouserFields = true;
        break;
      case "Digikey":
        digikeyFields = true;
        break;
      case "TTI":
        ttiFields = true;
        break;
      case "Arrow":
        arrowFields = true;
        break;
      case "TI":
        tiFields = true;
        break;
      case "Element14":
        element14Fields = true;
        break;
      case "TME":
        tmeFields = true;
        break;
      case "Rutronik":
        rutronikFields = true;
        break;
      default:
        // Hide all fields for default case
        mouserFields = false;
        digikeyFields = false;
        ttiFields = false;
        arrowFields = false;
        tiFields = false;
        element14Fields = false;
        tmeFields = false;
        rutronikFields = false;
    }

    setFieldVisibility({
      mouserFields,
      digikeyFields,
      ttiFields,
      arrowFields,
      tiFields,
      element14Fields,
      tmeFields,
      rutronikFields,
    });
  };

  // Handle form field changes
  const handleFormFieldChange = (field, value) => {
    setDistributorForm((prev) => ({ ...prev, [field]: value }));
  };

  // Handle save trigger click
  const handleTriggerClick = useCallback((type) => {
    if (type === "SUBMIT") {
      setFormTriggers((prev) => ({
        ...prev,
        submitTrigger: prev.submitTrigger + 1,
      }));
    }
  }, []);

  // Handle form submit (save functionality)
  const handleSubmit = useCallback(
    async ({ values, errors }) => {
      // INFO: check for errors and return if errors found
      if (errors.length > 0) return;

      // INFO: construct setting payload
      let savePayload = {};
      for (let i = 0; i < values.length; i++) {
        savePayload[values[i].name] = values[i].value;
        savePayload["company_id"] = user?.company_id;
      }

      setIsLoading(true);

      try {
        // INFO: call save API (using the same endpoint as original)
        const saveResponse = await saveDistributorConfigAPI(savePayload);

        // INFO: check API response and show relevant alert message
        if (saveResponse.status) {
          iziToast.success({
            title: "Success",
            message: "Distributor configuration saved successfully",
            position: "topCenter",
          });

          // Call onSaveSuccess callback if provided
          if (onSaveSuccess && typeof onSaveSuccess === "function") {
            onSaveSuccess();
          }
        } else {
          iziToast.error({
            title: "Error",
            message:
              saveResponse.message ||
              "Failed to save distributor configuration",
            position: "topCenter",
          });
        }
      } catch (error) {
        iziToast.error({
          title: "Error",
          message: "Something went wrong, please try again later.",
          position: "topCenter",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [user?.company_id, onSaveSuccess],
  );

  // Generate auth URLs
  const getDigikeyAuthUrl = () => {
    const redirectUrl = replacePlaceholders(
      "{{API_URL}}/digikey/callback?config={{companyId}}_{{distributorId}}_{{token}}",
      {
        API_URL,
        companyId: initialValues?.company_id || user?.company_id || "1",
        token: authToken,
        distributorId: distributorRecordId,
      },
    );

    const digikeyBaseUrl =
      distributorForm.digikey_environment === 2
        ? "https://sandbox-api.digikey.com"
        : "https://api.digikey.com";

    // ENCODED. The redirect carries its own query string (?config=…), so
    // pasting it raw let its `?` and `&` merge into DigiKey's query — the
    // provider then read a truncated redirect_uri and the rest as its own
    // parameters. A Hub JWT in `config` also carries `.` and `-`; encoding
    // keeps the whole value one parameter.
    return replacePlaceholders(
      digikeyBaseUrl +
        "/v1/oauth2/authorize?response_type=code&client_id={{clientId}}&redirect_uri=" +
        encodeURIComponent(redirectUrl),
      { clientId: distributorForm.digikey_client_id },
    );
  };

  const getElement14AuthUrl = () => {
    const element14Config = replacePlaceholders(
      "{{companyId}}_{{distributorId}}_{{token}}",
      {
        companyId: initialValues?.company_id || user?.company_id || "1",
        distributorId: distributorRecordId,
        token: authToken,
      },
    );

    return replacePlaceholders(
      `{{API_URL}}/element14/auth?api_key={{apiKey}}&config={{config}}`,
      {
        API_URL,
        apiKey: distributorForm.element14_generate_token_api_key,
        config: element14Config,
      },
    );
  };

  // Convert form to values array for submission (same format as original)
  const getFormValuesForSubmission = useCallback(() => {
    return Object.keys(distributorForm).map((key) => ({
      name: key,
      value: distributorForm[key],
    }));
  }, [distributorForm]);

  // Handle selectedDistributor prop changes
  useEffect(() => {
    if (selectedDistributor && selectedDistributor !== distributorForm.type) {
      setDistributorForm((prev) => ({ ...prev, type: selectedDistributor }));
      handleDistributorTypeChange(selectedDistributor);
    }
  }, [selectedDistributor, distributorForm.type]);

  // When editing, pre-fill the form with the distributor's saved values.
  const NUMERIC_FIELDS = [
    "status",
    "mouser_order_environment",
    "element14_environment",
    "digikey_environment",
    "ti_order_environment",
  ];
  useEffect(() => {
    if (!initialValues || typeof initialValues !== "object") return;
    setDistributorForm((prev) => {
      const merged = { ...prev };
      Object.keys(prev).forEach((key) => {
        const val = initialValues[key];
        if (val === undefined || val === null) return;
        merged[key] = NUMERIC_FIELDS.includes(key)
          ? Number(val) || prev[key]
          : val;
      });
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // Trigger submit when formTriggers changes
  useEffect(() => {
    if (formTriggers.submitTrigger > 0) {
      const values = getFormValuesForSubmission();
      const errors = []; // You can add validation logic here
      handleSubmit({ values, errors });
    }
  }, [formTriggers.submitTrigger, getFormValuesForSubmission, handleSubmit]);

  const cfg = FIELD_CONFIG[distributorForm.type] || null;
  const envOptions = cfg?.envKey ? ENV_BY_KEY[cfg.envKey] || [] : [];
  const oauthUrl =
    distributorForm.type === "Digikey"
      ? getDigikeyAuthUrl()
      : distributorForm.type === "Element14"
        ? getElement14AuthUrl()
        : null;

  // Already authorized? (has a saved token) → show the connected state instead
  // of "Not connected".
  const oauthConnected =
    distributorForm.type === "Digikey"
      ? Boolean(
          distributorForm.digikey_access_token ||
            distributorForm.digikey_refresh_token,
        )
      : distributorForm.type === "Element14"
        ? Boolean(distributorForm.element14_auth_token)
        : false;

  return (
    <div className="space-y-6">
      {/* Hidden save button for modal save functionality */}
      {isModal && (
        <button
          data-save-distributor-config
          onClick={() => handleTriggerClick("SUBMIT")}
          className="hidden"
        />
      )}

      {isModal ? (
        // Modal layout — matches the prototype's configure modal.
        <div className="space-y-5">
          {/* Status (segmented) + Environment */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-s360-fg2">Status</Label>
              <div className="inline-flex w-full rounded-md border border-s360-stroke1 bg-s360-app p-0.5">
                <button
                  type="button"
                  onClick={() => handleFormFieldChange("status", 1)}
                  className={`flex-1 h-8 rounded text-sm font-semibold transition-colors ${
                    distributorForm.status === 1
                      ? "bg-s360-violet text-white shadow-sm"
                      : "text-s360-fg2 hover:text-s360-fg1"
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => handleFormFieldChange("status", 2)}
                  className={`flex-1 h-8 rounded text-sm font-semibold transition-colors ${
                    distributorForm.status === 2
                      ? "bg-s360-violet text-white shadow-sm"
                      : "text-s360-fg2 hover:text-s360-fg1"
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>

            {cfg?.envKey && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-s360-fg2">
                  {cfg.envLabel}
                </Label>
                <Select
                  value={distributorForm[cfg.envKey]?.toString()}
                  onValueChange={(value) =>
                    handleFormFieldChange(cfg.envKey, parseInt(value))
                  }
                >
                  <SelectTrigger className="h-10 border-s360-stroke1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {envOptions.map((env) => (
                      <SelectItem key={env.value} value={env.value.toString()}>
                        {env.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* API credentials */}
          <div className="space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
              API credentials
            </div>
            <div className="space-y-4">
              {(cfg?.fields || []).map((f) => (
                <div className="space-y-1.5" key={f.key}>
                  <Label className="inline-flex items-center gap-1 text-xs font-semibold text-s360-fg2">
                    {f.label}
                    {f.secret && <Lock className="w-3 h-3 text-s360-fg3" />}
                  </Label>
                  <SecretInput
                    secret={f.secret}
                    value={distributorForm[f.key] || ""}
                    onChange={(v) => handleFormFieldChange(f.key, v)}
                    placeholder={f.ph}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* OAuth authorization */}
          {oauthUrl && (
            <div
              className={`rounded-md border p-3 ${
                oauthConnected
                  ? "border-[#A7E3B0] bg-[#F2FBF5]"
                  : "border-[#FFE08A] bg-[#FFF8E1]"
              }`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`w-8 h-8 shrink-0 rounded-md inline-flex items-center justify-center bg-white border ${
                    oauthConnected
                      ? "text-s360-success border-[#A7E3B0]"
                      : "text-s360-warning border-[#FFE08A]"
                  }`}
                >
                  {oauthConnected ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Link2 className="w-4 h-4" />
                  )}
                </span>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-semibold text-s360-fg1">
                    {cfg?.oauthLabel} authorization
                  </span>
                  <span className="text-xs text-s360-fg3">
                    {oauthConnected
                      ? "Connected — token valid"
                      : "Not connected — authorize to enable ordering"}
                  </span>
                </div>
                <button
                  type="button"
                  title={showOauthUrl ? "Hide link" : "Show link"}
                  aria-label={showOauthUrl ? "Hide link" : "Show link"}
                  onClick={() => setShowOauthUrl((s) => !s)}
                  className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-md border border-s360-stroke1 bg-white text-s360-fg2 hover:bg-s360-bg2 hover:text-s360-violet"
                >
                  {showOauthUrl ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-8 shrink-0 rounded-md border border-s360-stroke1 bg-white text-s360-fg1 hover:bg-s360-bg2"
                  onClick={() => window.open(oauthUrl, "_blank")}
                >
                  <Link2 className="w-3.5 h-3.5 mr-1.5" />
                  {oauthConnected ? "Reauthorize" : "Authorize"}
                </Button>
              </div>

              {showOauthUrl && (
                <div className="mt-3 rounded border border-s360-stroke1 bg-white p-2 text-[11px] leading-[16px] font-mono text-s360-fg2 break-all select-all">
                  {oauthUrl}
                </div>
              )}
            </div>
          )}

          {/* Legacy per-distributor fields (kept hidden — superseded by the config-driven layout above) */}
          <div className="hidden">
            {/* Mouser Fields */}
            {fieldVisibility.mouserFields && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="mouser-api-key">Mouser API Key</Label>
                  <Input
                    type="text"
                    placeholder="Enter the Mouser API key"
                    value={distributorForm.mouser_api_key}
                    onChange={(e) =>
                      handleFormFieldChange("mouser_api_key", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mouser-order-api-key">
                    Mouser Order API Key
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter the Mouser order API key"
                    value={distributorForm.mouser_order_api_key}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "mouser_order_api_key",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mouser-order-env">
                    Mouser Order Environment
                  </Label>
                  <Select
                    value={distributorForm.mouser_order_environment?.toString()}
                    onValueChange={(value) =>
                      handleFormFieldChange(
                        "mouser_order_environment",
                        parseInt(value),
                      )
                    }
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select Order Environment" />
                    </SelectTrigger>
                    <SelectContent>
                      {MOUSER_ORDER_ENVIRONMENT.map((env) => (
                        <SelectItem
                          key={env.value}
                          value={env.value.toString()}
                        >
                          {env.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* TTI Fields */}
            {fieldVisibility.ttiFields && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="tti-api-key">TTI API Key</Label>
                  <Input
                    type="text"
                    placeholder="Enter the TTI API key"
                    value={distributorForm.tti_api_key}
                    onChange={(e) =>
                      handleFormFieldChange("tti_api_key", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tti-customer-account">
                    TTI Customer Account Number
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter the TTI customer account number"
                    value={distributorForm.tti_customer_account_number}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "tti_customer_account_number",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>
              </>
            )}

            {/* Arrow Fields */}
            {fieldVisibility.arrowFields && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="arrow-api-key">Arrow API Key</Label>
                  <Input
                    type="text"
                    placeholder="Enter the Arrow API key"
                    value={distributorForm.arrow_api_key}
                    onChange={(e) =>
                      handleFormFieldChange("arrow_api_key", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="arrow-api-username">Arrow API Username</Label>
                  <Input
                    type="text"
                    placeholder="Enter the Arrow API username"
                    value={distributorForm.arrow_api_username}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "arrow_api_username",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>
              </>
            )}

            {/* Element14 Fields */}
            {fieldVisibility.element14Fields && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="element14-api-key">
                    Search Product API Key
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter the Search Product API Key"
                    value={distributorForm.element14_api_key}
                    onChange={(e) =>
                      handleFormFieldChange("element14_api_key", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="element14-generate-token-key">
                    Generate Auth Token API Key
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter the Element14 generate token API key"
                    value={distributorForm.element14_generate_token_api_key}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "element14_generate_token_api_key",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="element14-trade-account">
                    Element14 Trade Account
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter Element14 Trade Account"
                    value={distributorForm.element14_trade_account}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "element14_trade_account",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="element14-auth-token">
                    Element14 Auth Token
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter Element14 Auth Token"
                    value={distributorForm.element14_auth_token}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "element14_auth_token",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="element14-token-datetime">
                    Element14 Token Date Time
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter the Element14 token date time"
                    value={distributorForm.element14_token_date_time}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "element14_token_date_time",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="element14-auth">Element14 Auth</Label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter the Element14 Auth"
                      value={getElement14AuthUrl()}
                      readOnly
                      className="h-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2"
                      onClick={() =>
                        window.open(getElement14AuthUrl(), "_blank")
                      }
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Digikey Fields */}
            {fieldVisibility.digikeyFields && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="digikey-client-id">Digikey Client ID</Label>
                  <Input
                    type="text"
                    placeholder="Enter the Digikey client ID"
                    value={distributorForm.digikey_client_id}
                    onChange={(e) =>
                      handleFormFieldChange("digikey_client_id", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="digikey-secret">Digikey Secret</Label>
                  <Input
                    type="text"
                    placeholder="Enter the Digikey secret"
                    value={distributorForm.digikey_secret}
                    onChange={(e) =>
                      handleFormFieldChange("digikey_secret", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="digikey-access-token">
                    Digikey Access Token
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter the Digikey access token"
                    value={distributorForm.digikey_access_token}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "digikey_access_token",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="digikey-token-datetime">
                    Digikey Token Created Date Time
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter the Digikey access token created date time"
                    value={distributorForm.digikey_token_date_time}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "digikey_token_date_time",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="digikey-token-expiry">
                    Digikey Token Expiry
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter the Digikey access token expiry"
                    value={distributorForm.digikey_token_expiry}
                    onChange={(e) =>
                      handleFormFieldChange(
                        "digikey_token_expiry",
                        e.target.value,
                      )
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="digikey-environment">
                    Digikey Environment
                  </Label>
                  <Select
                    value={distributorForm.digikey_environment?.toString()}
                    onValueChange={(value) =>
                      handleFormFieldChange(
                        "digikey_environment",
                        parseInt(value),
                      )
                    }
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select Digikey Environment" />
                    </SelectTrigger>
                    <SelectContent>
                      {DIGIKEY_ENVIRONMENT.map((env) => (
                        <SelectItem
                          key={env.value}
                          value={env.value.toString()}
                        >
                          {env.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="digikey-auth">Digikey Auth</Label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter the Digikey auth link"
                      value={getDigikeyAuthUrl()}
                      readOnly
                      className="h-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2"
                      onClick={() => window.open(getDigikeyAuthUrl(), "_blank")}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* TME Fields */}
            {fieldVisibility.tmeFields && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="tme-token">TME Token</Label>
                  <Input
                    type="text"
                    placeholder="Enter the TME Token"
                    value={distributorForm.tme_token}
                    onChange={(e) =>
                      handleFormFieldChange("tme_token", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tme-secret">TME Secret</Label>
                  <Input
                    type="text"
                    placeholder="Enter the TME Secret"
                    value={distributorForm.tme_secret}
                    onChange={(e) =>
                      handleFormFieldChange("tme_secret", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tme-order-email">TME Order Email</Label>
                  <Input
                    type="email"
                    placeholder="Enter the TME Order Email"
                    value={distributorForm.tme_order_email}
                    onChange={(e) =>
                      handleFormFieldChange("tme_order_email", e.target.value)
                    }
                    className="h-10"
                  />
                </div>
              </>
            )}

            {/* Rutronik Fields */}
            {fieldVisibility.rutronikFields && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="rutronik-api-key">Rutronik Api Key</Label>
                  <Input
                    type="text"
                    placeholder="Enter the Rutronik Api Key"
                    value={distributorForm.rutronik_api_key}
                    onChange={(e) =>
                      handleFormFieldChange("rutronik_api_key", e.target.value)
                    }
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rutronik-order-email">Rutronik Order Email</Label>
                  <Input
                    type="email"
                    placeholder="Enter the Rutronik Order Email"
                    value={distributorForm.rutronik_order_email}
                    onChange={(e) =>
                      handleFormFieldChange("rutronik_order_email", e.target.value)
                    }
                    className="h-10"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        // Original layout for non-modal mode
        <Card className="p-6 bg-card border overflow-hidden relative rounded-lg shadow-sm stats-card text-card-foreground border-t-[4px] border-t-blue-600">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Distributor Configuration
              </h2>
              <Button
                onClick={() => handleTriggerClick("SUBMIT")}
                disabled={isLoading}
                className="min-w-[100px] h-9 rounded-md bg-blue-600 text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-60"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="distributor-status">Status</Label>
                  <Select
                    value={distributorForm.status?.toString()}
                    onValueChange={(value) =>
                      handleFormFieldChange("status", parseInt(value))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS.map((status) => (
                        <SelectItem
                          key={status.value}
                          value={status.value.toString()}
                        >
                          {status.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="distributor-type">Type</Label>
                  <Select
                    value={distributorForm.type}
                    onValueChange={handleDistributorTypeChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Distributor Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DISTRIBUTOR_STATUS.map((distributor) => (
                        <SelectItem
                          key={distributor.value}
                          value={distributor.value}
                        >
                          {distributor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mouser-order-env">
                    Mouser Order Environment
                  </Label>
                  <Select
                    value={distributorForm.mouser_order_environment?.toString()}
                    onValueChange={(value) =>
                      handleFormFieldChange(
                        "mouser_order_environment",
                        parseInt(value),
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Order Environment" />
                    </SelectTrigger>
                    <SelectContent>
                      {MOUSER_ORDER_ENVIRONMENT.map((env) => (
                        <SelectItem
                          key={env.value}
                          value={env.value.toString()}
                        >
                          {env.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              </div>

              {/* API Keys */}
              <div className="space-y-5">
                {fieldVisibility.mouserFields && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="mouser-api-key">Mouser Api Key</Label>
                      <Input
                        type="text"
                        placeholder="Enter the Mouser api key"
                        value={distributorForm.mouser_api_key}
                        onChange={(e) =>
                          handleFormFieldChange(
                            "mouser_api_key",
                            e.target.value,
                          )
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mouser-order-api-key">
                        Mouser Order Api Key
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter the Mouser order api key"
                        value={distributorForm.mouser_order_api_key}
                        onChange={(e) =>
                          handleFormFieldChange(
                            "mouser_order_api_key",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </>
                )}

                {fieldVisibility.ttiFields && (
                  <div className="space-y-2">
                    <Label htmlFor="tti-api-key">TTI Api Key</Label>
                    <Input
                      type="text"
                      placeholder="Enter the TTI api key"
                      value={distributorForm.tti_api_key}
                      onChange={(e) =>
                        handleFormFieldChange("tti_api_key", e.target.value)
                      }
                    />
                  </div>
                )}

                {fieldVisibility.arrowFields && (
                  <div className="space-y-2">
                    <Label htmlFor="arrow-api-key">Arrow Api Key</Label>
                    <Input
                      type="text"
                      placeholder="Enter the Arrow api key"
                      value={distributorForm.arrow_api_key}
                      onChange={(e) =>
                        handleFormFieldChange("arrow_api_key", e.target.value)
                      }
                    />
                  </div>
                )}

                {fieldVisibility.element14Fields && (
                  <div className="space-y-2">
                    <Label htmlFor="element14-api-key">
                      Search Product Api Key
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter the Search Product Api Key"
                      value={distributorForm.element14_api_key}
                      onChange={(e) =>
                        handleFormFieldChange(
                          "element14_api_key",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DistributorConfiguration;
